import { type JsonValue, type Schema, type SchemaDocument } from "../../common/schema/ir";

// The payload a schema asks for, with nothing filled in: the point is to hand somebody the shape rather than a
// plausible record, so a string is empty and a number is zero, and every nested object and array is built out too —
// a scaffold that stops at `{}` for the first object inside it is one nobody can see the shape of.
export function samplePayload(doc: SchemaDocument): JsonValue {
  return build(doc.root, doc, new Set());
}

function build(schema: Schema, doc: SchemaDocument, visiting: Set<string>): JsonValue {
  // Whatever the schema itself says it should be is what it should be, whatever its type would have given.
  if (schema.default !== undefined) return schema.default;

  switch (schema.kind) {
    case "unknown":
    case "never":
    case "null":
      return null;

    case "boolean":
      return false;

    case "number":
      return 0;

    case "string":
      return "";

    case "literal":
      return schema.value;

    case "enum":
      return schema.values.length > 0 ? schema.values[0] : null;

    case "ref": {
      // A model holding one of itself has no finished sample, so the second time round it is left as null.
      if (visiting.has(schema.name)) return null;
      const target = doc.defs.find((def) => def.name === schema.name)?.schema;
      if (!target) return null;
      visiting.add(schema.name);
      const value = build(target, doc, visiting);
      visiting.delete(schema.name);
      return value;
    }

    case "array": {
      const prefix = schema.prefix ?? [];
      const items = prefix.map((item) => build(item, doc, visiting));
      // One element past the tuple even when none is required, since an empty array shows none of what goes in it.
      const wanted = Math.max(schema.minItems ?? 0, schema.items.kind === "unknown" ? 0 : prefix.length + 1);
      const room = schema.maxItems ?? wanted;
      while (items.length < Math.min(wanted, room)) items.push(build(schema.items, doc, visiting));
      return items;
    }

    case "object": {
      // No prototype, so a property called `__proto__` is written as the key it is rather than swallowed as a setter.
      const out: { [key: string]: JsonValue } = Object.create(null);
      // Optional properties are written out too: a scaffold is for filling in, and a key that is not there is a key
      // nobody knows to write. What is left out is the open end of the object, which names nothing to write.
      for (const property of schema.properties) out[property.name] = build(property.schema, doc, visiting);
      return out;
    }

    case "union": {
      // The shape is worth more than the emptiness: `Optional[Address]` builds the address rather than the null.
      const first = schema.options.find((option) => option.kind !== "null") ?? schema.options[0];
      return first ? build(first, doc, visiting) : null;
    }

    case "intersection": {
      const parts = schema.parts.map((part) => build(part, doc, visiting));
      // Two object halves of one intersection are one object; anything else has no merge, so the first part stands.
      if (parts.every(isPlainObject)) return Object.assign(Object.create(null), ...parts);
      return parts[0] ?? null;
    }
  }
}

function isPlainObject(value: JsonValue): value is { [key: string]: JsonValue } {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
